import { API_URL } from "./CONSTANT";
import toast from "../lib/toast";

// Friendly names so a failed upload reads "...the shade report..." not "...shade...".
const REPORT_LABELS = {
  dxf: "DXF file",
  shade: "shade report",
  facet: "facet report",
  electricity: "production report",
};

const normalizeBearerToken = (token) => {
  if (typeof token !== "string") return "";
  const trimmed = token.trim();
  if (!trimmed) return "";
  return trimmed.toLowerCase().startsWith("bearer ")
    ? trimmed
    : `Bearer ${trimmed}`;
};

export const resolveReportProjectId = (currentDesign, fallbackId = "") => {
  return (
    fallbackId ||
    currentDesign?.uuid ||
    currentDesign?.id ||
    currentDesign?.project_id ||
    ""
  );
};

// Show a single, de-duplicated toast when a report fails to reach the backend.
// `notifyOnError` lets headless routes opt out (they surface failure through
// their own status controller and have no ToastHost mounted anyway).
// `retry` re-runs the exact same upload so the user can retry without
// regenerating the report.
const notifyReportUploadFailure = (reportType, status, retry) => {
  const label = REPORT_LABELS[reportType] || "report";
  const description =
    status && status !== 0
      ? `Server responded with ${status}.`
      : "Couldn't reach the server.";
  toast.error(`Couldn't share the ${label}.`, {
    key: `report-upload-${reportType}`,
    description: `${description} It was generated locally but not saved to your project.`,
    duration: 10000,
    action: typeof retry === "function" ? { label: "Retry upload", onClick: retry } : null,
  });
};

export const uploadDesignerReport = async (params) => {
  const {
    token,
    projectId,
    reportType,
    fileBlob,
    fileName,
    baseUrlOverride,
    notifyOnError = true,
  } = params;

  const authHeader = normalizeBearerToken(token);
  if (!authHeader || !projectId || !reportType || !fileBlob) {
    // A genuine no-op (e.g. background task with nothing to upload) — stay quiet.
    return { ok: false, skipped: true, status: 0 };
  }

  const formData = new FormData();
  formData.append("file", fileBlob, fileName || `report-${reportType}`);
  const baseApiUrl = String(baseUrlOverride || API_URL || "").replace(/\/+$/, "");

  // Retry re-invokes the upload with the same params (blob included), so the
  // report never has to be regenerated.
  const retry = () => uploadDesignerReport(params);

  try {
    const response = await fetch(
      `${baseApiUrl}/designer/${projectId}/reports/${reportType}`,
      {
        method: "POST",
        headers: {
          Authorization: authHeader,
        },
        body: formData,
      }
    );

    if (!response.ok && notifyOnError) {
      notifyReportUploadFailure(reportType, response.status, retry);
    }

    return {
      ok: response.ok,
      skipped: false,
      status: response.status,
      body: await response.text().catch(() => ""),
    };
  } catch (error) {
    if (notifyOnError) notifyReportUploadFailure(reportType, 0, retry);
    return { ok: false, skipped: false, status: 0, error };
  }
};
